/**
 * Centralized API service with JWT authentication.
 * All API calls go through this module instead of direct fetch().
 */
const API_BASE = process.env.REACT_APP_API_URL || '';

// Store tokens in memory for immediate access
let currentAccessToken = localStorage.getItem('access_token') || null;
let currentRefreshToken = localStorage.getItem('refresh_token') || null;

const getHeaders = (includeAuth = true) => {
  const headers = { 'Content-Type': 'application/json' };
  if (includeAuth && currentAccessToken) {
    headers['Authorization'] = `Bearer ${currentAccessToken}`;
  }
  return headers;
};

const refreshAccessToken = async () => {
  if (!currentRefreshToken) return null;
  try {
    const response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: currentRefreshToken }),
    });
    if (response.ok) {
      const data = await response.json();
      api.setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    }
  } catch (e) {
    console.error('Token refresh failed:', e);
  }
  return null;
};

const handleResponse = async (response) => {
  if (response.status === 401 && currentRefreshToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return null; // Signal to retry
    }
    api.clearTokens();
    window.location.href = '/';
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    const errorDetail = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
    throw new Error(errorDetail || `HTTP ${response.status}`);
  }
  return response.json();
};

const api = {
  setTokens: (access, refresh) => {
    currentAccessToken = access;
    currentRefreshToken = refresh;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    console.log('[API] Tokens set successfully');
  },

  clearTokens: () => {
    currentAccessToken = null;
    currentRefreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  getToken: () => currentAccessToken,

  get: async (url, params = {}) => {
    const query = Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const fullUrl = `${API_BASE}${url}${query ? '?' + query : ''}`;
    const response = await fetch(fullUrl, { headers: getHeaders() });
    const result = await handleResponse(response);
    if (result === null) {
      const retryResponse = await fetch(fullUrl, { headers: getHeaders() });
      return retryResponse.json();
    }
    return result;
  },

  post: async (url, body = {}) => {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    const result = await handleResponse(response);
    if (result === null) {
      const retryResponse = await fetch(`${API_BASE}${url}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      return retryResponse.json();
    }
    return result;
  },

  put: async (url, body = {}) => {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    const result = await handleResponse(response);
    if (result === null) {
      const retryResponse = await fetch(`${API_BASE}${url}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      return retryResponse.json();
    }
    return result;
  },

  delete: async (url, body = null) => {
    const options = { method: 'DELETE', headers: getHeaders() };
    if (body) options.body = JSON.stringify(body);
    const response = await fetch(`${API_BASE}${url}`, options);
    const result = await handleResponse(response);
    if (result === null) {
      const retryResponse = await fetch(`${API_BASE}${url}`, options);
      return retryResponse.json();
    }
    return result;
  },

  // File upload with FormData (for Excel/CSV imports)
  upload: async (url, formData) => {
    const headers = {};
    if (currentAccessToken) {
      headers['Authorization'] = `Bearer ${currentAccessToken}`;
    }
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    // Handle error responses
    if (!response.ok) {
      let errorDetail = 'Unknown error';
      try {
        const errorData = await response.json();
        const detail = errorData.detail || errorData;
        errorDetail = typeof detail === 'string' ? detail : (detail.message || JSON.stringify(detail));
      } catch (e) {
        errorDetail = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorDetail);
    }

    return response.json();
  },

  // Auth-specific endpoints (no auth header needed)
  auth: {
    register: (data) =>
      fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(r => r.json()),

    login: (data) =>
      fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(r => r.json()),

    refresh: (refreshToken) =>
      fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).then(r => r.json()),

    forgotPassword: (email) =>
      fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).then(r => r.json()),

    resetPassword: (token, newPassword) =>
      fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      }).then(r => r.json()),
  },
};

export default api;
